import {
  ArrowTopRightOnSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { HABIT_TEMPLATES } from '../constants'
import { CURATED_DEEPLINK_PRESETS } from '../constants/deeplinkPresets'
import type {
  DayOfWeek,
  HabitFormData,
  HabitFrequency,
  SubHabitFormData,
} from '../interfaces'
import { DAYS_OF_WEEK } from '../utils/dateHelpers'
import {
  formatDeeplinkDisplay,
  isValidDeeplink,
  openDeeplink,
} from '../utils/deeplink'
import {
  isDeeplinkInPresets,
  mapHabitCategoryToDeeplinkCategory,
} from '../utils/deeplinkCatalogFilter'
import {
  DEFAULT_HABIT_CATEGORY,
  HABIT_CATEGORIES,
} from '../utils/habitCategories'
import { useDeeplinkCatalog } from '../utils/useDeeplinkCatalog'
import {
  DeeplinkCatalogSheet,
  type InitialCatalogFormState,
} from './DeeplinkCatalogSheet'
import { EmojiNameInput } from './EmojiNameInput'
import { ModalBackdrop } from './ModalBackdrop'

interface HabitFormSheetProps {
  initial: HabitFormData | null
  onSubmit: (data: HabitFormData) => Promise<void>
  onClose: () => void
}

const FREQUENCY_OPTIONS: { label: string; value: HabitFrequency }[] = [
  { label: 'Daily', value: 'daily' },
  { label: 'Specific Days', value: 'weekly_days' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'X per week', value: 'x_per_week' },
]

/** Sub-habits support daily/weekly_days/x_per_week */
const SUB_FREQUENCY_OPTIONS: { label: string; value: HabitFrequency }[] = [
  { label: 'Daily', value: 'daily' },
  { label: 'Specific Days', value: 'weekly_days' },
  { label: 'X per week', value: 'x_per_week' },
]

function makeEmptySubHabit(): SubHabitFormData {
  return {
    name: '',
    description: '',
    frequency_type: 'daily',
    weekly_days: [],
    deeplink_url: '',
  }
}

export function HabitFormSheet({
  initial,
  onSubmit,
  onClose,
}: HabitFormSheetProps): ReactElement {
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [category, setCategory] = useState(
    initial?.category ?? DEFAULT_HABIT_CATEGORY,
  )
  const [frequencyType, setFrequencyType] = useState<HabitFrequency>(
    initial?.frequency_type ?? 'x_per_week',
  )
  const [frequencyTarget, setFrequencyTarget] = useState(
    initial?.frequency_target ?? 3,
  )
  const [weeklyDays, setWeeklyDays] = useState<DayOfWeek[]>(
    initial?.weekly_days ?? [],
  )
  const [deeplinkUrl, setDeeplinkUrl] = useState(initial?.deeplink_url ?? '')
  const [catalogTarget, setCatalogTarget] = useState<'main' | number | null>(
    null,
  )
  const [initialCatalogForm, setInitialCatalogForm] =
    useState<InitialCatalogFormState | null>(null)

  const { customPresets, communityPresets } = useDeeplinkCatalog()

  const allCatalogPresets = useMemo(() => {
    return [...CURATED_DEEPLINK_PRESETS, ...customPresets, ...communityPresets]
  }, [customPresets, communityPresets])

  const isMainDeeplinkCustomAndUnsaved = useMemo(() => {
    if (!isValidDeeplink(deeplinkUrl)) return false
    return !isDeeplinkInPresets(deeplinkUrl, allCatalogPresets)
  }, [deeplinkUrl, allCatalogPresets])

  function handleCreateCustomDeeplink(
    target: 'main' | number,
    url: string,
    suggestedName?: string,
    suggestedCategory?: string,
    suggestedIcon?: string,
  ): void {
    setCatalogTarget(target)
    setInitialCatalogForm({
      isOpen: true,
      scheme_url: url.trim(),
      name: suggestedName?.trim() || formatDeeplinkDisplay(url),
      category: mapHabitCategoryToDeeplinkCategory(suggestedCategory),
      icon: suggestedIcon || '📱',
    })
  }

  function handleOpenCatalog(target: 'main' | number): void {
    setCatalogTarget(target)
    setInitialCatalogForm(null)
  }

  function handleCloseCatalog(): void {
    setCatalogTarget(null)
    setInitialCatalogForm(null)
  }

  const [subHabits, setSubHabits] = useState<SubHabitFormData[]>(
    initial?.subHabits ?? [],
  )
  const [saving, setSaving] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  const isCreateMode = initial === null

  useEffect(() => {
    nameRef.current?.focus()
  }, [])

  function applyTemplate(template: (typeof HABIT_TEMPLATES)[number]): void {
    setSelectedTemplate(template.label)
    setName(template.name ? `${template.emoji} ${template.name}` : '')
    setDescription(template.description)
    setCategory(template.category)
    setFrequencyType(template.frequency_type)
    setTimeout(() => nameRef.current?.focus(), 50)
  }

  function addSubHabit(): void {
    setSubHabits((prev) => [...prev, makeEmptySubHabit()])
  }

  function removeSubHabit(index: number): void {
    setSubHabits((prev) => prev.filter((_, i) => i !== index))
  }

  function updateSubHabit(
    index: number,
    patch: Partial<SubHabitFormData>,
  ): void {
    setSubHabits((prev) =>
      prev.map((sh, i) => (i === index ? { ...sh, ...patch } : sh)),
    )
  }

  function toggleDay(day: DayOfWeek): void {
    setWeeklyDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    )
  }

  function toggleSubHabitDay(index: number, day: DayOfWeek): void {
    const sh = subHabits[index]
    const currentDays = sh.weekly_days ?? []
    const updated = currentDays.includes(day)
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day]
    updateSubHabit(index, { weekly_days: updated })
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!name.trim()) return

    if (frequencyType === 'weekly_days' && weeklyDays.length === 0) return

    // Validate: sub-habits must have names and valid weekly_days if selected
    if (subHabits.some((sh) => !sh.name.trim())) return
    if (
      subHabits.some(
        (sh) =>
          sh.frequency_type === 'weekly_days' &&
          (!sh.weekly_days || sh.weekly_days.length === 0),
      )
    )
      return

    setSaving(true)
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim(),
        category,
        frequency_type: frequencyType,
        deeplink_url: deeplinkUrl.trim() || undefined,
        ...((frequencyType === 'x_per_week' || subHabits.length > 0) && {
          frequency_target: frequencyTarget,
        }),
        ...(frequencyType === 'weekly_days' && {
          weekly_days: weeklyDays,
        }),
        ...(subHabits.length > 0 && {
          subHabits: subHabits.map((sh) => ({
            ...sh,
            name: sh.name.trim(),
            description: sh.description.trim(),
            deeplink_url: sh.deeplink_url?.trim() || undefined,
            ...(sh.frequency_type === 'x_per_week' && {
              frequency_target: sh.frequency_target ?? 3,
            }),
            ...(sh.frequency_type === 'weekly_days' && {
              weekly_days: sh.weekly_days ?? [],
            }),
          })),
        }),
      })
    } finally {
      setSaving(false)
    }
  }

  const isSubmitDisabled =
    saving ||
    !name.trim() ||
    (frequencyType === 'weekly_days' && weeklyDays.length === 0) ||
    subHabits.some((sh) => !sh.name.trim()) ||
    subHabits.some(
      (sh) =>
        sh.frequency_type === 'weekly_days' &&
        (!sh.weekly_days || sh.weekly_days.length === 0),
    )

  return (
    <>
      <ModalBackdrop ariaLabel="Close form" onClose={onClose} />
      <div className="fixed inset-0 z-50 m-auto h-fit max-h-[90dvh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-2xl bg-white px-4 pt-6 pb-8 shadow-2xl sm:shadow-md">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 text-lg">
            {initial ? 'Edit Habit' : 'New Habit'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {isCreateMode && (
          <div className="mb-4">
            <p className="mb-2 font-medium text-gray-500 text-xs uppercase tracking-wide">
              Templates
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {HABIT_TEMPLATES.map((template) => (
                <button
                  key={template.label}
                  type="button"
                  onClick={() => applyTemplate(template)}
                  className={`flex shrink-0 flex-col items-center gap-0.5 rounded-xl px-3 py-2.5 font-medium text-xs transition-colors ${
                    selectedTemplate === template.label
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span className="text-xl">{template.emoji}</span>
                  <span>{template.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Name */}
          <div>
            <label
              htmlFor="habit-name"
              className="mb-1 block font-medium text-gray-700 text-sm"
            >
              Name <span className="text-red-500">*</span>
            </label>
            <EmojiNameInput
              inputRef={nameRef}
              value={name}
              onChange={setName}
            />
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="habit-desc"
              className="mb-1 block font-medium text-gray-700 text-sm"
            >
              Description
            </label>
            <textarea
              id="habit-desc"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Optional notes…"
              rows={2}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {/* Category */}
          <div>
            <label
              htmlFor="habit-category"
              className="mb-1 block font-medium text-gray-700 text-sm"
            >
              Category
            </label>
            <select
              id="habit-category"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {HABIT_CATEGORIES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Parent frequency — always shown; determines when parent is "done" */}
          <div>
            <label
              htmlFor="habit-freq"
              className="mb-1 block font-medium text-gray-700 text-sm"
            >
              Frequency
            </label>
            <select
              id="habit-freq"
              value={frequencyType}
              onChange={(event) =>
                setFrequencyType(event.target.value as HabitFrequency)
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {FREQUENCY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Specific weekly days selector */}
          {frequencyType === 'weekly_days' && (
            <div>
              <p className="mb-1.5 block font-medium text-gray-700 text-sm">
                Select days <span className="text-red-500">*</span>
              </p>
              <div className="flex gap-1.5 overflow-x-auto">
                {DAYS_OF_WEEK.map((day) => {
                  const selected = weeklyDays.includes(day.key)
                  return (
                    <button
                      key={day.key}
                      type="button"
                      onClick={() => toggleDay(day.key)}
                      className={`flex-1 rounded-xl py-2 font-semibold text-sm transition-colors ${
                        selected
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {day.short}
                    </button>
                  )
                })}
              </div>
              {weeklyDays.length === 0 && (
                <p className="mt-1 text-red-500 text-xs">
                  Please select at least one day
                </p>
              )}
            </div>
          )}

          {/* Times per week target (only when frequency is x_per_week) */}
          {frequencyType === 'x_per_week' && (
            <div>
              <label
                htmlFor="habit-target"
                className="mb-1 block font-medium text-gray-700 text-sm"
              >
                Times per week target
              </label>
              <input
                id="habit-target"
                type="number"
                min={1}
                max={99}
                value={frequencyTarget}
                onChange={(event) =>
                  setFrequencyTarget(
                    Math.max(1, Math.min(99, Number(event.target.value))),
                  )
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          )}

          {/* ── App Deeplink (Optional) ── */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label
                htmlFor="habit-deeplink"
                className="font-medium text-gray-700 text-sm"
              >
                App Deeplink (optional)
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleOpenCatalog('main')}
                  className="flex items-center gap-1 font-medium text-indigo-600 text-xs hover:text-indigo-800"
                >
                  <span>📱</span>
                  <span>Browse Catalog</span>
                </button>
                {isValidDeeplink(deeplinkUrl) && (
                  <button
                    type="button"
                    onClick={() => openDeeplink(deeplinkUrl)}
                    className="flex items-center gap-1 font-medium text-indigo-600 text-xs hover:text-indigo-800"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    Test link
                  </button>
                )}
              </div>
            </div>
            <input
              id="habit-deeplink"
              type="text"
              value={deeplinkUrl}
              onChange={(e) => setDeeplinkUrl(e.target.value)}
              placeholder="e.g. kindle://, duolingo://, shortcuts://..., or https://..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            {isMainDeeplinkCustomAndUnsaved ? (
              <div className="mt-1.5 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/80 px-2.5 py-1.5 text-amber-900 text-xs">
                <span className="flex items-center gap-1.5 truncate text-amber-800">
                  <span>✨</span>
                  <span>Custom deeplink not in catalog</span>
                </span>
                <button
                  type="button"
                  onClick={() =>
                    handleCreateCustomDeeplink(
                      'main',
                      deeplinkUrl,
                      name,
                      category,
                      name.match(/^\p{Extended_Pictographic}/u)?.[0] ?? '📱',
                    )
                  }
                  className="ml-2 flex shrink-0 items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1 font-medium text-white text-xs hover:bg-amber-700 active:bg-amber-800"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  Create custom deeplink
                </button>
              </div>
            ) : (
              <p className="mt-1 text-gray-500 text-xs">
                Automatically opens this app or link on your device when this
                habit is checked off.
              </p>
            )}
          </div>

          {/* ── Sub-habits section ── */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="font-medium text-gray-700 text-sm">Sub-habits</p>
              <button
                type="button"
                onClick={addSubHabit}
                className="flex items-center gap-1 rounded-lg bg-indigo-50 px-2.5 py-1 font-medium text-indigo-600 text-xs hover:bg-indigo-100"
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add sub-habit
              </button>
            </div>

            {subHabits.length === 0 ? (
              <p className="rounded-xl border border-gray-200 border-dashed px-3 py-4 text-center text-gray-400 text-sm">
                No sub-habits yet. Tap "Add sub-habit" to break this habit into
                smaller goals.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {subHabits.map((sh, index) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: sub-habits do not have stable ids until saved
                    key={index}
                    className="rounded-xl border border-gray-200 bg-gray-50 p-3"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className="font-medium text-gray-500 text-xs">
                        Sub-habit {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeSubHabit(index)}
                        aria-label="Remove sub-habit"
                        className="rounded-full p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* Sub-habit name */}
                    <input
                      type="text"
                      value={sh.name}
                      onChange={(e) =>
                        updateSubHabit(index, { name: e.target.value })
                      }
                      placeholder="Name *"
                      required
                      className="mb-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />

                    {/* Sub-habit description */}
                    <input
                      type="text"
                      value={sh.description}
                      onChange={(e) =>
                        updateSubHabit(index, { description: e.target.value })
                      }
                      placeholder="Description (optional)"
                      className="mb-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />

                    {/* Sub-habit frequency */}
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <select
                          value={sh.frequency_type}
                          onChange={(e) =>
                            updateSubHabit(index, {
                              frequency_type: e.target.value as HabitFrequency,
                            })
                          }
                          className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                          {SUB_FREQUENCY_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>

                        {sh.frequency_type === 'x_per_week' && (
                          <input
                            type="number"
                            min={1}
                            max={7}
                            value={sh.frequency_target ?? 3}
                            onChange={(e) =>
                              updateSubHabit(index, {
                                frequency_target: Math.max(
                                  1,
                                  Math.min(7, Number(e.target.value)),
                                ),
                              })
                            }
                            placeholder="×/wk"
                            className="w-20 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        )}
                      </div>

                      {sh.frequency_type === 'weekly_days' && (
                        <div className="flex gap-1">
                          {DAYS_OF_WEEK.map((day) => {
                            const selected = (sh.weekly_days ?? []).includes(
                              day.key,
                            )
                            return (
                              <button
                                key={day.key}
                                type="button"
                                onClick={() =>
                                  toggleSubHabitDay(index, day.key)
                                }
                                className={`flex-1 rounded-lg py-1 font-semibold text-xs transition-colors ${
                                  selected
                                    ? 'bg-indigo-600 text-white'
                                    : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                {day.short}
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>

                    {/* Sub-habit deeplink */}
                    <div className="mt-2">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-gray-500 text-xs">
                          App deeplink (optional)
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleOpenCatalog(index)}
                            className="flex items-center gap-0.5 font-medium text-[11px] text-indigo-600 hover:text-indigo-800"
                          >
                            <span>📱</span>
                            <span>Catalog</span>
                          </button>
                          {isValidDeeplink(sh.deeplink_url) && (
                            <button
                              type="button"
                              onClick={() => openDeeplink(sh.deeplink_url)}
                              className="flex items-center gap-0.5 font-medium text-[11px] text-indigo-600 hover:text-indigo-800"
                            >
                              <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                              Test
                            </button>
                          )}
                        </div>
                      </div>
                      <input
                        type="text"
                        value={sh.deeplink_url ?? ''}
                        onChange={(e) =>
                          updateSubHabit(index, {
                            deeplink_url: e.target.value,
                          })
                        }
                        placeholder="e.g. duolingo:// or kindle://"
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                      {isValidDeeplink(sh.deeplink_url) &&
                        !isDeeplinkInPresets(
                          sh.deeplink_url,
                          allCatalogPresets,
                        ) && (
                          <div className="mt-1 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50/80 px-2 py-1 text-[11px] text-amber-900">
                            <span className="truncate text-amber-800">
                              Not in catalog
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                handleCreateCustomDeeplink(
                                  index,
                                  sh.deeplink_url ?? '',
                                  sh.name,
                                  category,
                                  sh.name.match(
                                    /^\p{Extended_Pictographic}/u,
                                  )?.[0] ?? '📱',
                                )
                              }
                              className="ml-1.5 flex shrink-0 items-center gap-0.5 rounded bg-amber-600 px-1.5 py-0.5 font-medium text-white hover:bg-amber-700 active:bg-amber-800"
                            >
                              <PlusIcon className="h-3 w-3" />
                              Create custom deeplink
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitDisabled}
            className="mt-1 w-full rounded-xl bg-indigo-600 px-4 py-3 font-semibold text-base text-white active:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Habit'}
          </button>
        </form>
      </div>

      <DeeplinkCatalogSheet
        isOpen={catalogTarget !== null}
        onClose={handleCloseCatalog}
        initialForm={initialCatalogForm}
        currentUrl={
          catalogTarget === 'main'
            ? deeplinkUrl
            : typeof catalogTarget === 'number'
              ? (subHabits[catalogTarget]?.deeplink_url ?? '')
              : ''
        }
        onSelect={(scheme) => {
          if (catalogTarget === 'main') {
            setDeeplinkUrl(scheme)
          } else if (typeof catalogTarget === 'number') {
            updateSubHabit(catalogTarget, { deeplink_url: scheme })
          }
        }}
      />
    </>
  )
}
